import { useUserInfoStore } from '@/modules/login/store/userInfo';
import { useEffect, useState } from 'react';
import { LIVE_PLAN } from './mock';
import type { CorporatePlan } from './types';

/**
 * «ویزیت آنلاین سازمانی» is rolled out to ONE account: the signed-in user whose user id is listed below AND
 * whose mobile number hashes to the hash next to it. Both must match. Nobody else — no other account, no
 * device flag, no query parameter — ever sees it.
 *
 * The number itself is never in the code: only the SHA-256 of its normalised form (`09xxxxxxxxx`).
 * Everything is decided on the client after mount, so server rendering and the shared caches stay
 * exactly as they are for everyone (docs/performance.md: withCSR stays user-independent).
 */
export const CORPORATE_MEMBER_PHONE_SHA256: readonly string[] = ['177c0384061cc78d8f01a03681bd4f8cf8b3dd13620f44e693306f9c6409eaf2'];

/** The same account's paziresh24 user id. */
export const CORPORATE_MEMBER_USER_IDS: readonly string[] = ['23489442'];

const PERSIAN = '۰۱۲۳۴۵۶۷۸۹';
const ARABIC = '٠١٢٣٤٥٦٧٨٩';

/** `+98 935…`, `0098935…`, `935…`, Persian digits → `0935…`. */
export const normalizeIranMobile = (value?: string | null) => {
  const digits = String(value ?? '')
    .replace(/[۰-۹]/g, d => String(PERSIAN.indexOf(d)))
    .replace(/[٠-٩]/g, d => String(ARABIC.indexOf(d)))
    .replace(/\D/g, '');
  if (digits.startsWith('0098')) return `0${digits.slice(4)}`;
  if (digits.startsWith('98') && digits.length === 12) return `0${digits.slice(2)}`;
  if (digits.startsWith('9') && digits.length === 10) return `0${digits}`;
  return digits;
};

const sha256 = async (text: string) => {
  const bytes = await window.crypto.subtle.digest('SHA-256', new TextEncoder().encode(text));
  return Array.from(new Uint8Array(bytes), b => b.toString(16).padStart(2, '0')).join('');
};

// One hash per phone for the whole page: every search card asks.
const decided = new Map<string, Promise<boolean>>();
const isMemberPhone = (phone: string) => {
  if (!decided.has(phone)) {
    decided.set(
      phone,
      sha256(phone)
        .then(hash => CORPORATE_MEMBER_PHONE_SHA256.includes(hash))
        .catch(() => false),
    );
  }
  return decided.get(phone)!;
};

export interface CorporateMember {
  plan: CorporatePlan;
  firstName: string;
}

/** The corporate plan of the signed-in user, or `null` for everyone else (and always on the server). */
export const useCorporateMember = (): CorporateMember | null => {
  const isLogin = useUserInfoStore(state => state.isLogin);
  const userId = useUserInfoStore(state => state.info?.id);
  const cell = useUserInfoStore(state => state.info?.cell);
  const firstName = useUserInfoStore(state => state.info?.name);
  const [member, setMember] = useState(false);

  useEffect(() => {
    let active = true;
    setMember(false);
    const phone = normalizeIranMobile(cell);
    if (!isLogin || !CORPORATE_MEMBER_USER_IDS.includes(String(userId ?? '')) || phone.length !== 11 || !window.crypto?.subtle) return;
    isMemberPhone(phone).then(yes => active && setMember(yes));
    return () => {
      active = false;
    };
  }, [isLogin, userId, cell]);

  return member ? { plan: LIVE_PLAN, firstName: firstName?.trim() || '' } : null;
};
